import React, { useCallback } from "react";
import { useMyAccountUK } from "../../context";
import { deleteCookie, getCookie } from "../../utils/cookies";
import { isSpecificSite } from "../../utils/isSpecificSite";
import { Button } from "../common";
import LinkNoStyle from "../common/LinkNoStyle/LinkNoStyle";
import * as styles from "./SponsorMeButton.module.scss";

const BASKET_LIMIT = 4;

interface SponsorMeButtonProps {
	title: string;
	withDivider?: boolean;
	sadDog: DogsTrust.SADCookie;
	isH2?: boolean;
	onSponsor: (navigate: boolean) => void;
	onRemove: () => void;
}

export const SponsorMeButton: React.FC<SponsorMeButtonProps> = ({
	title,
	withDivider,
	sadDog,
	onSponsor,
	onRemove,
	isH2 = false,
}) => {
	isSpecificSite("uk", "SponsorMeButton");
	const { profile, handleAddToBasket, handleRemoveFromBasket } =
		useMyAccountUK();

	const amountInBasket = profile.basket.filter(
		(dog) => dog.name === sadDog.name,
	).length;

	const isBasketFullOfOtherDogs =
		profile.basket.length >= BASKET_LIMIT && amountInBasket === 0;

	const existsInSadBasket = amountInBasket > 0;

	const getButtonText = () => {
		if (amountInBasket > 0 && profile.basket.length < BASKET_LIMIT) {
			return "Add again";
		}
		if (isBasketFullOfOtherDogs) {
			return "Basket is full";
		}
		if (profile.basket.length >= BASKET_LIMIT) {
			return "Remove from basket";
		}
		return "Add to basket";
	};

	const buttonVariant =
		!isBasketFullOfOtherDogs || amountInBasket > 0 ? "secondary" : "primary";

	const onClick = useCallback(async () => {
		// Clear the basket cookie if it exists (i.e. once we've cleared the basket after finishing a sponsorship,
		// we don't want to remove newly added items)
		if (getCookie("clear_basket")) {
			deleteCookie("clear_basket");
		}
		if (isBasketFullOfOtherDogs) {
			return;
		}
		if (existsInSadBasket && profile.basket.length >= BASKET_LIMIT) {
			onRemove();
			handleRemoveFromBasket(sadDog);
		} else if (existsInSadBasket && profile.basket.length < BASKET_LIMIT) {
			onSponsor(false);
			handleAddToBasket(sadDog);
		} else {
			onSponsor(true);
			handleAddToBasket(sadDog);
		}
	}, [
		isBasketFullOfOtherDogs,
		existsInSadBasket,
		profile.basket,
		sadDog,
		onSponsor,
		onRemove,
		handleAddToBasket,
		handleRemoveFromBasket,
	]);

	return (
		<div className={styles.sponsormebuttoncontainer}>
			{withDivider && <div className={styles.sectionbreak} />}
			{isH2 ? <h2>{title}</h2> : <h3>{title}</h3>}

			<Button
				onClick={onClick}
				variant={buttonVariant}
				disabled={isBasketFullOfOtherDogs}
			>
				{getButtonText()}
			</Button>
			{existsInSadBasket && (
				<LinkNoStyle to={process.env.GATSBY_SAD_BASKET_URL}>
					Continue to basket
				</LinkNoStyle>
			)}
		</div>
	);
};
